import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { Logo } from "@/components/Logo";
import { spring, useUITheme, ThemeToggle, pageTheme } from "@/components/ios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Billing() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const { dark, toggle } = useUITheme();
  const t = pageTheme(dark);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || state === "loading") return;
    setState("loading");
    try {
      await axios.post(`${API}/billing/portal`, { email, origin_url: window.location.origin });
      setState("done");
    } catch {
      setState("error");
    }
  };

  return (
    <main className={`min-h-screen flex items-center justify-center px-5 py-10 font-sans ${t.page}`} data-testid="billing-page" data-theme={dark ? "dark" : "light"}>
      <div className="fixed top-5 right-5 z-40">
        <ThemeToggle dark={dark} onToggle={toggle} testid="billing-theme-toggle" />
      </div>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={spring}
        className={`rounded-[32px] p-8 md:p-12 max-w-md w-full ${t.card}`}
      >
        <div className="mb-8">
          <Logo />
        </div>
        <h1 className={`font-bold tracking-tight text-2xl md:text-[28px] mb-3 ${t.primary}`} data-testid="billing-title">Manage your subscription.</h1>
        <p className={`text-[15px] ${t.secondary} leading-relaxed mb-7`}>
          Enter the email you used at checkout. We'll send a secure Stripe link to update your
          payment method, download invoices, or cancel — it expires shortly for your security.
        </p>
        {state === "done" ? (
          <div className={`rounded-sm border p-4 ${t.noticeBox}`} data-testid="billing-success">
            <p className={`text-[15px] font-medium ${t.noticeText}`}>
              If a subscription exists for this email, a management link is on its way to your inbox.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="billing-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Checkout email"
              data-testid="billing-email-input"
              className={`w-full min-h-[48px] rounded-xl border border-transparent px-4 text-[16px] focus:outline-none focus:ring-2 transition-[background-color,box-shadow] duration-200 ${t.input}`}
            />
            <button
              type="submit"
              disabled={state === "loading"}
              data-testid="billing-submit-button"
              className={`w-full min-h-[48px] rounded-full ${t.primaryBtn} text-[15px] font-semibold flex items-center justify-center gap-2 disabled:opacity-40 active:scale-[0.98] transition-transform duration-150`}
            >
              {state === "loading" ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> : (<>Send management link <ArrowRight className="w-4 h-4" strokeWidth={2} /></>)}
            </button>
          </form>
        )}
        {state === "error" && (
          <p className="text-[13px] font-medium text-[#FF3B30] mt-3" data-testid="billing-error">Something went wrong — try again.</p>
        )}
        <Link to="/" data-testid="billing-back-link"
          className={`inline-flex items-center gap-2 text-[14px] ${t.linkMuted} transition-colors duration-200 mt-8 min-h-[44px]`}>
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back to Doclyx
        </Link>
      </motion.div>
    </main>
  );
}
